/** GENERATED_ASSET — geometry kit: paths whose bounds are exact, not estimated. */
import type { VisualBounds } from './types';

export interface Pt { x: number; y: number }

/** Trimmed fixed-precision number (style.coordinatePrecision = 3). */
export function fmt(v: number, prec = 3): string {
  if (!Number.isFinite(v)) throw new Error(`Non-finite coordinate: ${v}`);
  const s = (Math.round(v * 10 ** prec) / 10 ** prec).toFixed(prec);
  const t = s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
  return t === '-0' ? '0' : t;
}

export class Bounds {
  x0 = Infinity; y0 = Infinity; x1 = -Infinity; y1 = -Infinity;
  get empty(): boolean { return this.x0 > this.x1; }
  add(x: number, y: number, pad = 0): this {
    this.x0 = Math.min(this.x0, x - pad); this.y0 = Math.min(this.y0, y - pad);
    this.x1 = Math.max(this.x1, x + pad); this.y1 = Math.max(this.y1, y + pad);
    return this;
  }
  addBox(x0: number, y0: number, x1: number, y1: number, pad = 0): this {
    return this.add(x0, y0, pad).add(x1, y1, pad);
  }
  merge(o: Bounds): this { if (!o.empty) this.addBox(o.x0, o.y0, o.x1, o.y1); return this; }
  /** Outward-rounded to 1/1000 so the reported box always contains the paint. */
  toRect(): VisualBounds {
    if (this.empty) return { x: 0, y: 0, width: 0, height: 0 };
    const x = Math.floor(this.x0 * 1000) / 1000, y = Math.floor(this.y0 * 1000) / 1000;
    const r = Math.ceil(this.x1 * 1000) / 1000, b = Math.ceil(this.y1 * 1000) / 1000;
    return { x, y, width: Math.round((r - x) * 1000) / 1000, height: Math.round((b - y) * 1000) / 1000 };
  }
}

const quadAt = (a: number, b: number, c: number, t: number) => (1 - t) ** 2 * a + 2 * (1 - t) * t * b + t * t * c;
const cubAt = (a: number, b: number, c: number, d: number, t: number) =>
  (1 - t) ** 3 * a + 3 * (1 - t) ** 2 * t * b + 3 * (1 - t) * t * t * c + t ** 3 * d;

function cubicRoots(p0: number, p1: number, p2: number, p3: number): number[] {
  const a = -p0 + 3 * p1 - 3 * p2 + p3, b = 2 * (p0 - 2 * p1 + p2), c = p1 - p0;
  const out: number[] = [];
  if (Math.abs(a) < 1e-12) { if (Math.abs(b) > 1e-12) out.push(-c / b); }
  else {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) { const s = Math.sqrt(disc); out.push((-b + s) / (2 * a), (-b - s) / (2 * a)); }
  }
  return out.filter((t) => t > 0 && t < 1);
}

export class Path {
  readonly bounds = new Bounds();
  private cmds: string[] = [];
  private cx = 0; private cy = 0; private sx = 0; private sy = 0;
  M(x: number, y: number): this {
    this.cmds.push(`M${fmt(x)} ${fmt(y)}`); this.cx = this.sx = x; this.cy = this.sy = y;
    this.bounds.add(x, y); return this;
  }
  L(x: number, y: number): this {
    this.cmds.push(`L${fmt(x)} ${fmt(y)}`); this.cx = x; this.cy = y; this.bounds.add(x, y); return this;
  }
  Q(qx: number, qy: number, x: number, y: number): this {
    this.cmds.push(`Q${fmt(qx)} ${fmt(qy)} ${fmt(x)} ${fmt(y)}`);
    for (const [a, b, c, isX] of [[this.cx, qx, x, true], [this.cy, qy, y, false]] as const) {
      const den = a - 2 * b + c;
      if (Math.abs(den) > 1e-12) {
        const t = (a - b) / den;
        if (t > 0 && t < 1) {
          const v = quadAt(a, b, c, t);
          if (isX) this.bounds.add(v, this.cy); else this.bounds.add(this.cx, v);
        }
      }
    }
    this.cx = x; this.cy = y; this.bounds.add(x, y); return this;
  }
  C(x1: number, y1: number, x2: number, y2: number, x: number, y: number): this {
    this.cmds.push(`C${fmt(x1)} ${fmt(y1)} ${fmt(x2)} ${fmt(y2)} ${fmt(x)} ${fmt(y)}`);
    const { cx, cy } = this;
    for (const t of cubicRoots(cx, x1, x2, x)) this.bounds.add(cubAt(cx, x1, x2, x, t), cubAt(cy, y1, y2, y, t));
    for (const t of cubicRoots(cy, y1, y2, y)) this.bounds.add(cubAt(cx, x1, x2, x, t), cubAt(cy, y1, y2, y, t));
    this.cx = x; this.cy = y; this.bounds.add(x, y); return this;
  }
  Z(): this { this.cmds.push('Z'); this.cx = this.sx; this.cy = this.sy; return this; }
  poly(pts: readonly Pt[], close = true): this {
    pts.forEach((p, i) => (i === 0 ? this.M(p.x, p.y) : this.L(p.x, p.y)));
    return close ? this.Z() : this;
  }
  /** Closed/open Catmull-Rom through points → cubic segments (organic, exact bounds). */
  smooth(pts: readonly Pt[], close = true, tension = 0.5): this {
    const n = pts.length;
    if (n < 3) return this.poly(pts, close);
    const at = (i: number): Pt => (close ? pts[(i + n) % n]! : pts[Math.max(0, Math.min(n - 1, i))]!);
    this.M(pts[0]!.x, pts[0]!.y);
    const segs = close ? n : n - 1;
    const k = tension / 3 * 2;
    for (let i = 0; i < segs; i++) {
      const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
      this.C(p1.x + (p2.x - p0.x) * k / 2, p1.y + (p2.y - p0.y) * k / 2,
        p2.x - (p3.x - p1.x) * k / 2, p2.y - (p3.y - p1.y) * k / 2, p2.x, p2.y);
    }
    return close ? this.Z() : this;
  }
  d(): string { return this.cmds.join(''); }
}

/** Rounded rectangle path with per-corner cubic arcs (exact bounds). */
export function rrect(x0: number, y0: number, x1: number, y1: number, r: number | [number, number, number, number]): Path {
  const [tl, tr, br, bl] = typeof r === 'number' ? [r, r, r, r] : r;
  const k = 0.5523;
  const p = new Path();
  p.M(x0 + tl, y0).L(x1 - tr, y0).C(x1 - tr + tr * k, y0, x1, y0 + tr - tr * k, x1, y0 + tr);
  p.L(x1, y1 - br).C(x1, y1 - br + br * k, x1 - br + br * k, y1, x1 - br, y1);
  p.L(x0 + bl, y1).C(x0 + bl - bl * k, y1, x0, y1 - bl + bl * k, x0, y1 - bl);
  p.L(x0, y0 + tl).C(x0, y0 + tl - tl * k, x0 + tl - tl * k, y0, x0 + tl, y0);
  return p.Z();
}

/** Sampled elliptical arc (degrees, y-down) as polyline points. */
export function arcPoints(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, n = 24): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const a = ((a0 + (a1 - a0) * (i / n)) * Math.PI) / 180;
    pts.push({ x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry });
  }
  return pts;
}
